/**
 * Frontend interaction tracking utility
 * Sends interaction events to the backend API for logging
 */

import { buildApiUrl } from './api';

/**
 * Log trend interaction (expand, view, etc.)
 */
export const logTrendInteraction = async (trendId, interactionType, interactionData = {}) => {
  try {
    const response = await fetch(buildApiUrl('/api/interactions/trend/'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        trend_id: trendId,
        interaction_type: interactionType,
        interaction_data: interactionData
      })
    });
    
    if (!response.ok) {
      console.warn('Failed to log trend interaction:', response.status, response.statusText);
    }
  } catch (error) {
    // Log error but don't interrupt user experience
    console.warn('Failed to log trend interaction:', error);
  }
};

/**
 * Log carousel interaction (next, prev, click, etc.)
 */
export const logCarouselInteraction = async (trendId, cardIndex, cardType, action) => {
  try {
    const response = await fetch(buildApiUrl('/api/interactions/carousel/'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        trend_id: trendId,
        card_index: cardIndex,
        card_type: cardType,
        action: action
      })
    });
    
    if (!response.ok) {
      console.warn('Failed to log carousel interaction:', response.status, response.statusText);
    }
  } catch (error) {
    // Log error but don't interrupt user experience
    console.warn('Failed to log carousel interaction:', error);
  }
};

/**
 * Log video interaction (play, pause, stop, ended).
 */
export const logVideoInteraction = async (trendId, action, videoPositionSeconds = null, videoDurationSeconds = null, watchPercentage = null) => {
  try {
    const response = await fetch(buildApiUrl('/api/interactions/video/'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        trend_id: trendId,
        action: action,
        video_position_seconds: videoPositionSeconds,
        video_duration_seconds: videoDurationSeconds,
        watch_percentage: watchPercentage
      })
    });
    
    if (!response.ok) {
      console.warn('Failed to log video interaction:', response.status, response.statusText);
    }
  } catch (error) {
    // Log error but don't interrupt user experience
    console.warn('Failed to log video interaction:', error);
  }
};

/**
 * Log trend impression (when trend card is viewed/scrolled into view).
 */
export const logTrendImpression = async (trendId, verticalId, cardPosition, scrollSet = null, viewportPosition = 'visible') => {
  try {
    console.log('[logTrendImpression] Sending request:', {
      trend_id: trendId,
      vertical_id: verticalId,
      card_position: cardPosition,
      scroll_set: scrollSet,
      viewport_position: viewportPosition
    });
    
    const response = await fetch(buildApiUrl('/api/impressions/trend/'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        trend_id: trendId,
        vertical_id: verticalId,
        card_position: cardPosition,
        scroll_set: scrollSet,
        viewport_position: viewportPosition
      })
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.warn('Failed to log trend impression:', response.status, response.statusText, errorText);
    } else {
      const result = await response.json();
      console.log('[logTrendImpression] Success:', result);
    }
  } catch (error) {
    // Log error but don't interrupt user experience
    console.error('Failed to log trend impression:', error);
  }
};

/**
 * Log vertical impression (when vertical card is viewed).
 */
export const logVerticalImpression = async (verticalId, categoryId, feedPosition, viewportPosition = 'visible') => {
  try {
    const response = await fetch(buildApiUrl('/api/impressions/vertical/'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        vertical_id: verticalId,
        category_id: categoryId,
        feed_position: feedPosition,
        viewport_position: viewportPosition
      })
    });
    
    if (!response.ok) {
      console.warn('Failed to log vertical impression:', response.status, response.statusText);
    }
  } catch (error) {
    // Log error but don't interrupt user experience
    console.warn('Failed to log vertical impression:', error);
  }
};

/**
 * Log category impression (when category card is viewed on home page).
 */
export const logCategoryImpression = async (categoryId, cardPosition, viewportPosition = 'visible') => {
  try {
    console.log('[logCategoryImpression] Sending request:', {
      category_id: categoryId,
      card_position: cardPosition,
      viewport_position: viewportPosition
    });
    
    const response = await fetch(buildApiUrl('/api/impressions/category/'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',  // Include session cookie
      body: JSON.stringify({
        category_id: categoryId,
        card_position: cardPosition,
        viewport_position: viewportPosition
      })
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.warn('Failed to log category impression:', response.status, response.statusText, errorText);
    } else {
      const result = await response.json();
      console.log('[logCategoryImpression] Success:', result);
    }
  } catch (error) {
    // Log error but don't interrupt user experience
    console.error('Failed to log category impression:', error);
  }
};

/**
 * Log category interaction (when user clicks or hovers over category).
 */
export const logCategoryInteraction = async (categoryId, interactionType, interactionData = {}) => {
  try {
    console.log('[logCategoryInteraction] Sending request:', {
      category_id: categoryId,
      interaction_type: interactionType,
      interaction_data: interactionData
    });
    
    const response = await fetch(buildApiUrl('/api/interactions/category/'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',  // Include session cookie
      body: JSON.stringify({
        category_id: categoryId,
        interaction_type: interactionType,
        interaction_data: interactionData
      })
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.warn('Failed to log category interaction:', response.status, response.statusText, errorText);
    } else {
      const result = await response.json();
      console.log('[logCategoryInteraction] Success:', result);
    }
  } catch (error) {
    // Log error but don't interrupt user experience
    console.error('Failed to log category interaction:', error);
  }
};

